import React, { useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { useMutation } from '@apollo/client'
import { CREATE_WHITE_LABEL, UPDATE_WHITE_LABEL } from '../graphql/mutation'
import { GET_WHITE_LABELING_BY_ID } from '../graphql/query'
import { toast } from 'react-toastify'
import axios from 'axios'
import { log } from 'console'

interface IProps {
  closeModal: () => void
  data: any
  currentPlan: any
}

const FILE_UPLOAD_URL = process.env.REACT_APP_FILE_UPLOAD_URL

const WhiteLabelEditModal: React.FC<IProps> = ({ closeModal, data, currentPlan }) => {
  const [uploadFileUrl, setUploadFileUrl] = useState<string>('')
  const maxLength = 5000000 // 5MB
  const [createWhiteLabel] = useMutation(CREATE_WHITE_LABEL, {
    refetchQueries: [
      {
        query: GET_WHITE_LABELING_BY_ID,
        variables: {
          input: {
            bgAccountId: currentPlan?.bgAccountId
          }
        }
      }
    ]
  })
  const [updateWhiteLabel] = useMutation(UPDATE_WHITE_LABEL, {
    refetchQueries: [
      {
        query: GET_WHITE_LABELING_BY_ID,
        variables: {
          input: {
            bgAccountId: currentPlan?.bgAccountId
          }
        }
      }
    ]
  })

  const initialValues = {
    logo: data?.getWhiteLabelingbyId[0]?.logo || '/media/logos/botgo-logo-2nd.png',
    copyright: data?.getWhiteLabelingbyId[0]?.copyright || 'https://globtierinfotech.com/',
    companyName:
      data?.getWhiteLabelingbyId[0]?.companyName || 'Botgo Technologies Pvt Ltd. (by Globtier)'
  }

  // Validation schema using Yup
  const validationSchema = Yup.object().shape({
    logo: Yup.mixed().required('Logo is required'),
    // .test('fileType', 'Unsupported File Format', (value) => {
    //   return value && ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
    // }),
    copyright: Yup.string().max(255, 'Copyright text must be at most 255 characters'),
    // .required('Copyright is required'),
    companyName: Yup.string()
      .max(100, 'Company name must be at most 100 characters')
      .required('Company name is required')
  })

  const formatSizeInMB = (size: number) => {
    return (size / (1024 * 1024)).toFixed(2) + ' MB'
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      if (files[0].size <= maxLength) {
        try {
          // Upload the file
          const fileData = files[0]
          const data = {
            files: [
              {
                fileName: fileData.name,
                mimeType: fileData.type
              }
            ]
          }
          const response = await axios.post(FILE_UPLOAD_URL as string, data)
          const uploadURL = response.data[0].uploadURL

          await axios.put(uploadURL, fileData)
          let fileUrl = response.data[0].url
          setUploadFileUrl(fileUrl)
        } catch (err) {
          console.error(err)
        }
      } else {
        alert(`File ${files[0].name} exceeds the size limit ${formatSizeInMB(maxLength)}.`)
      }
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (data?.getWhiteLabelingbyId[0]?.id) {
        updateWhiteLabel({
          variables: {
            input: {
              id: data?.getWhiteLabelingbyId[0]?.id,
              bgAccountId: data?.getWhiteLabelingbyId[0]?.bgAccountId,
              logo: uploadFileUrl || values.logo,
              copyright: values.copyright,
              companyName: values.companyName
            }
          }
        })
          .then((res: any) => {
            if (res.data.updateWhiteLabeling.status === 200) {
              toast.success(res.data.updateWhiteLabeling.message)
            } else {
              toast.error('Unabel to update White Label')
            }
          })
          .catch((err) => {
            toast.error('Unabel to update White Label')
            console.log(err)
          })
      } else {
        createWhiteLabel({
          variables: {
            input: {
              bgAccountId: currentPlan?.bgAccountId,
              logo: values.logo,
              copyright: values.copyright,
              companyName: values.companyName
            }
          }
        })
          .then((res: any) => {
            if (res.data.createWhiteLabeling.status === 200) {
              toast.success('Successfully Created White Label')
            } else {
              toast.error('Unabel to create White Label')
            }
          })
          .catch((err) => {
            toast.error('Unabel to create White Label')
            console.log(err)
          })
      }
      closeModal()
    }
  })

  return (
    <div className='w-lg-500px p-5 p-lg-10 mx-auto'>
      <form onSubmit={formik.handleSubmit}>
        <div className='mb-10 text-center'>
          <h2 className='text-dark mb-4'>Edit White Label Settings</h2>
        </div>

        <div className='fv-row mb-7'>
          <label className='form-label fw-bolder text-dark fs-6 required'>Logo Upload:</label>
          <input
            type='file'
            accept='image/jpeg,image/png,image/gif'
            onChange={handleFileChange}
            className={clsx(
              'form-control form-control-lg',
              {
                'is-invalid': formik.touched.logo && formik.errors.logo
              },
              {
                'is-valid': formik.touched.logo && !formik.errors.logo
              }
            )}
          />
          {formik.touched.logo && formik.errors.logo && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{String(formik.errors.logo)}</span>
              </div>
            </div>
          )}
        </div>

        <div className='fv-row mb-7'>
          <label className='form-label fw-bolder text-dark fs-6 required'>Company Name:</label>
          <input
            type='text'
            {...formik.getFieldProps('companyName')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {
                'is-invalid': formik.touched.companyName && formik.errors.companyName
              },
              {
                'is-valid': formik.touched.companyName && !formik.errors.companyName
              }
            )}
            value={formik.values.companyName}
          />
          {formik.touched.companyName && formik.errors.companyName && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{String(formik.errors.companyName)}</span>
              </div>
            </div>
          )}
        </div>

        <div className='fv-row mb-7'>
          <label className='form-label fw-bolder text-dark fs-6'>Copyright Link:</label>
          <input
            type='text'
            {...formik.getFieldProps('copyright')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {
                'is-invalid': formik.touched.copyright && formik.errors.copyright
              },
              {
                'is-valid': formik.touched.copyright && !formik.errors.copyright
              }
            )}
            value={formik.values.copyright}
          />
          {formik.touched.copyright && formik.errors.copyright && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{String(formik.errors.copyright)}</span>
              </div>
            </div>
          )}
        </div>

        <div className='text-center'>
          <button
            type='submit'
            className='btn btn-lg btn-primary w-100 mb-5'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            Submit
          </button>
          <button
            type='button'
            className='btn btn-lg btn-secondary w-100 mb-5'
            onClick={closeModal}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}

export default WhiteLabelEditModal
